import { AnimatePresence, motion } from 'framer-motion';

export type BookingsPhase = 'climbing' | 'sold-out' | 'released';

interface Props {
  count: number;
  max: number;
  phase: BookingsPhase;
}

export function BookingsCounter({ count, max, phase }: Props) {
  const pct = Math.min(100, Math.round((count / max) * 100));
  const remaining = Math.max(0, max - count);

  if (phase === 'sold-out') {
    return (
      <motion.div
        initial={{ scale: 0.96 }}
        animate={{ scale: [1, 1.03, 1] }}
        transition={{ duration: 0.6, repeat: Infinity }}
        className="w-full bg-hit-gold/20 border-2 border-hit-gold rounded-lg p-5 text-center"
      >
        <p className="text-xs uppercase tracking-widest text-hit-gold font-bold mb-1">
          🚨 Cupos en tiempo real
        </p>
        <p className="text-hit-white text-2xl font-extrabold">¡CUPOS AGOTADOS!</p>
        <p className="text-hit-gray-light text-xs mt-1">
          {max} de {max} llamadas agendadas
        </p>
        <div className="h-2 w-full bg-hit-black rounded-full overflow-hidden border border-hit-gray-dark/40 mt-3">
          <div className="h-full bg-hit-gold w-full" />
        </div>
      </motion.div>
    );
  }

  if (phase === 'released') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full bg-emerald-950/40 border-2 border-emerald-500 rounded-lg p-4"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-widest text-emerald-400 font-bold">
            ✨ Cupo liberado para ti
          </span>
          <motion.span
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="text-xs font-bold text-emerald-400"
          >
            ● Reservado
          </motion.span>
        </div>

        <div className="text-hit-white text-lg font-extrabold mb-2">
          {count} <span className="text-hit-gray-mid font-medium">de {max} llamadas agendadas</span>
        </div>

        <div className="h-2 w-full bg-hit-black rounded-full overflow-hidden border border-hit-gray-dark/40">
          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
          <div className="h-full bg-emerald-400/40" style={{ width: '1%', marginLeft: `${pct}%`, marginTop: '-8px' }} />
        </div>

        <div className="mt-3 bg-hit-black/60 border border-emerald-700/40 rounded-md p-3">
          <p className="text-sm font-bold text-emerald-300 leading-tight">
            🎟️ Acabamos de liberar 1 cupo exclusivo para ti.
          </p>
          <p className="text-xs text-hit-gray-light mt-1 leading-snug">
            ⚠️ <strong className="text-hit-white">Si cierras esta página perderás este último cupo reservado.</strong>
            {' '}Agenda ahora abajo 👇
          </p>
        </div>
      </motion.div>
    );
  }

  // climbing
  return (
    <div className="w-full bg-hit-dark border border-hit-gray-dark/40 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-hit-gray-mid font-semibold">
          Cupos en tiempo real
        </span>
        <AnimatePresence mode="wait">
          <motion.span
            key={remaining}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.25 }}
            className="text-xs font-bold text-hit-gold"
          >
            {remaining} restantes
          </motion.span>
        </AnimatePresence>
      </div>

      <div className="text-hit-white text-lg font-extrabold mb-2">
        <AnimatePresence mode="wait">
          <motion.span
            key={count}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.3 }}
            className="inline-block"
          >
            {count}
          </motion.span>
        </AnimatePresence>
        <span className="text-hit-gray-mid font-medium"> de {max} llamadas agendadas</span>
      </div>

      <div className="h-2 w-full bg-hit-black rounded-full overflow-hidden border border-hit-gray-dark/40">
        <motion.div
          className="h-full bg-hit-gold"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>

      <p className="text-[11px] text-hit-gray-mid mt-2 text-center">
        🔥 Las plazas se llenan rápido. Asegura la tuya ahora.
      </p>
    </div>
  );
}
